import { useEffect, useState, useCallback } from "react";
import { Sparkles, Crown, Briefcase, Handshake, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual treatment per track. Keeps the widget identifiable at a glance
// without needing colour text everywhere.
const TRACK_META = {
  admin:   { label: "Admins",   icon: Crown,     accent: "text-amber-300",   bar: "bg-amber-400/80",   border: "border-amber-400/30" },
  manager: { label: "Managers", icon: Briefcase, accent: "text-blue-300",    bar: "bg-blue-400/80",    border: "border-blue-400/30" },
  rep:     { label: "Reps",     icon: Handshake, accent: "text-emerald-300", bar: "bg-emerald-400/80", border: "border-emerald-400/30" },
};

const TRACK_ORDER = ["admin", "manager", "rep"];

/**
 * Tiny dashboard card that surfaces per-step onboarding completion rates
 * across each role-track in the account. Turns the role-tailored onboarding
 * into a coaching tool — managers see exactly where new hires drop off
 * ("4/5 closers logged their first activity. 2/5 set their first goal.").
 *
 * Designed to render gracefully when the account has 0 or 1 users per track
 * (compact, no awkward 0% bars). Hides entirely if every track is empty.
 */
export function OnboardingCompletionWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API}/onboarding/team-progress`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Failed to fetch onboarding team progress:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  if (loading) {
    return (
      <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading onboarding progress…
        </div>
      </div>
    );
  }

  const tracks = data?.tracks || {};
  const visibleTracks = TRACK_ORDER.filter((k) => tracks[k]?.total_users > 0);
  if (visibleTracks.length === 0) return null;

  // Tip: skip the welcome + complete pseudo-steps when computing the
  // "where reps drop off" headline — they're trivially-completed bookends.
  // We still render them in the per-step bars below.
  const realStepsOnly = (steps) =>
    (steps || []).filter((s) => s.step_id !== "welcome" && s.step_id !== "complete");

  return (
    <div
      data-testid="onboarding-completion-widget"
      className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <h3 className="text-white font-semibold">Onboarding Progress</h3>
        </div>
        <span className="text-xs text-gray-500">Per-role completion</span>
      </div>

      <div className="space-y-5">
        {visibleTracks.map((trackKey) => {
          const meta = TRACK_META[trackKey];
          const TrackIcon = meta.icon;
          const t = tracks[trackKey];
          const realSteps = realStepsOnly(t.steps);
          // Find the biggest drop-off step so we can flag it visually.
          const lowestPct = realSteps.length > 0
            ? Math.min(...realSteps.map((s) => s.pct))
            : 100;

          return (
            <div key={trackKey} data-testid={`onboarding-track-${trackKey}`} className={`border ${meta.border} rounded-lg p-4 bg-black/20`}>
              {/* Track header — Iter185 (A04) shows three-state breakdown
                  (finished / skipped / in-progress) so "1/2 finished" +
                  "1 skipped" can no longer sneak past as "2/2 finished". */}
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <TrackIcon className={`w-4 h-4 ${meta.accent}`} />
                  <span className={`text-sm font-semibold ${meta.accent}`}>{meta.label}</span>
                  <span className="text-xs text-gray-500">
                    {t.completed_users}/{t.total_users} finished
                  </span>
                  {t.skipped_users > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20"
                      title="Reps who tapped Skip. They are NOT counted as finished."
                      data-testid={`onboarding-skipped-count-${trackKey}`}
                    >
                      {t.skipped_users} skipped
                    </span>
                  )}
                  {t.in_progress_users > 0 && (
                    <span
                      className="text-xs text-gray-500"
                      data-testid={`onboarding-inprogress-count-${trackKey}`}
                    >
                      · {t.in_progress_users} in progress
                    </span>
                  )}
                </div>
                <span
                  data-testid={`onboarding-completion-pct-${trackKey}`}
                  className={`text-lg font-bold ${meta.accent}`}
                >
                  {t.completion_pct}%
                </span>
              </div>

              {/* Per-step bars */}
              <div className="space-y-1.5">
                {realSteps.map((step) => {
                  const isLowest = realSteps.length > 1 && step.pct === lowestPct && step.pct < 100;
                  return (
                    <div
                      key={step.step_id}
                      data-testid={`onboarding-step-${step.step_id}`}
                      className="flex items-center gap-3 text-xs"
                    >
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {step.pct === 100 ? (
                          <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                        ) : (
                          <div className={`w-3 h-3 rounded-full border ${isLowest ? "border-amber-400/60" : "border-white/15"} shrink-0`} />
                        )}
                        <span className={`truncate ${isLowest ? "text-amber-300" : "text-gray-300"}`}>
                          {step.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 w-32 shrink-0">
                        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className={`h-full ${isLowest ? "bg-amber-400/80" : meta.bar} transition-all`}
                            style={{ width: `${step.pct}%` }}
                          />
                        </div>
                        <span className={`text-[10px] tabular-nums w-12 text-right ${isLowest ? "text-amber-300" : "text-gray-400"}`}>
                          {step.completed_count}/{step.total}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Drop-off hint */}
              {realSteps.length > 1 && lowestPct < 100 && (
                <div
                  data-testid={`onboarding-dropoff-${trackKey}`}
                  className="mt-3 pt-2.5 border-t border-white/5 text-[11px] text-gray-500"
                >
                  Biggest drop-off:{" "}
                  <span className="text-amber-300">
                    {realSteps.find((s) => s.pct === lowestPct)?.label}
                  </span>{" "}
                  — only {lowestPct}% finished
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
